<template><section><h3>API 简述</h3>
<p>1.所有的图表都是由 <code v-pre>点</code>、<code v-pre>线</code>、<code v-pre>面</code>、<code v-pre>文字</code> 构成，其中，Qcharts 中的<code v-pre>点</code>都是由<code v-pre>面</code>来绘制。即 Qcharts 中只有三种类型图形 <b><code v-pre>线</code>、<code v-pre>面</code>、<code v-pre>文字</code></b>。</p>
<p>2.Qcharts 中所有的 Visual 和 Plugin，都包含属性<code v-pre>attr</code>和样式<code v-pre>style</code>：</p>
<pre v-pre><code v-pre class="language-javascript"><span class="hljs-keyword">let</span> line = <span class="hljs-keyword">new</span> Line({ <span class="hljs-attr">smooth</span>: <span class="hljs-literal">true</span> })
</code></pre>
<p>如上代码：在初始化折线图的时候，<code v-pre>{smooth:true}</code> 就是属性 <code v-pre>attr</code>。</p>
<h5>通用属性</h5>
<p>Qcharts 中，Visual 与 Plugin 有一些通用属性 attr</p>
<table>
<thead>
<tr>
<th>名称</th>
<th>描述</th>
<th>值</th>
</tr>
</thead>
<tbody>
<tr>
<td>clientRect</td>
<td>位置信息</td>
<td>可以设置 top,bottom,left,right,widh,height</td>
</tr>
<tr>
<td>animation</td>
<td>动画属性</td>
<td>可以设置 duration,easing 等</td>
</tr>
</tbody>
</table>
<p>除了属性以外，visual 和 plugin 还有<code v-pre>style</code></p>
<pre v-pre><code v-pre class="language-javascript"><span class="hljs-comment">//point是一个比较特殊的样式，它除了面的基本属性外，它还有pointType属性，point是由Point插件来完成渲染的,详情查看 Plugin → Point</span>
line.style(<span class="hljs-string">'point'</span>, { <span class="hljs-attr">pointType</span>: <span class="hljs-string">'star'</span>, <span class="hljs-attr">angles</span>: <span class="hljs-number">5</span>, <span class="hljs-attr">innerRadius</span>: <span class="hljs-number">5</span>, <span class="hljs-attr">outerRadius</span>: <span class="hljs-number">10</span> })
</code></pre>
<p>如上代码：上面的代码将折线图的 point 类型设置为<code v-pre>star</code>，并设置了内外半径来控制大小。</p>
<p>对于有哪些属性<code v-pre>attr</code>设置，请具体参考具体的 api 文档说明，对于样式<code v-pre>style</code>的设置，<code v-pre>线</code>、<code v-pre>面</code>、<code v-pre>文字</code> 有一些通用属性，如下：</p>
<h5>线</h5>
<table>
<thead>
<tr>
<th>名称</th>
<th>描述</th>
<th>值</th>
</tr>
</thead>
<tbody>
<tr>
<td>strokeColor</td>
<td>线颜色</td>
<td>#f00 , rgba(0,0,0,0.5) , transparent</td>
</tr>
<tr>
<td>lineDash</td>
<td>虚线设置(默认不设置，不设置为实线)</td>
<td>[6,6,6] 实线部分和白线部分的大小依次是 6,6,6</td>
</tr>
<tr>
<td>lineCap</td>
<td>线条末端绘制样式</td>
<td>'round' ['butt','round','square'] // 平直边缘、圆形线冒、正方形线冒</td>
</tr>
<tr>
<td>lineJoin</td>
<td>两线条交汇处样式</td>
<td>'miter' ['bevel','round','miter'] //斜角、圆角、尖角</td>
</tr>
<tr>
<td>strokeColor</td>
<td>颜色值（支持 spritejs 中所有的颜色类型）</td>
<td>#f00 , rgba(0,0,0,0.5) , transparent</td>
</tr>
<tr>
<td>lineWidth</td>
<td>线宽(像素 px)</td>
<td>1 , 2 , 10</td>
</tr>
<tr>
<td>opacity</td>
<td>透明度(0~1)</td>
<td>1</td>
</tr>
<tr>
<td>translate</td>
<td>位置变换(像素数组)</td>
<td>[-10，-10]</td>
</tr>
<tr>
<td>display</td>
<td>展现类型'none'为不渲染</td>
<td>'','none'</td>
</tr>
</tbody>
</table>
<h5>面</h5>
<table>
<thead>
<tr>
<th>名称</th>
<th>描述</th>
<th>值</th>
</tr>
</thead>
<tbody>
<tr>
<td>strokeColor</td>
<td>边框颜色值</td>
<td>#f00 , rgba(0,0,0,0.5) , transparent</td>
</tr>
<tr>
<td>fillColor</td>
<td>填充颜色值</td>
<td>#f00 , rgba(0,0,0,0.5) , transparent</td>
</tr>
<tr>
<td>lineWidth</td>
<td>线宽(像素)</td>
<td>1 , 2 , 10</td>
</tr>
<tr>
<td>anchor</td>
<td>坐标中心</td>
<td>[0,0]</td>
</tr>
<tr>
<td>opacity</td>
<td>透明度(0~1)</td>
<td>1</td>
</tr>
<tr>
<td>translate</td>
<td>位置变换(像素数组)</td>
<td>[-10，-10]</td>
</tr>
<tr>
<td>display</td>
<td>展现类型'none'为不渲染</td>
<td>'','none'</td>
</tr>
</tbody>
</table>
<h5>文本</h5>
<table>
<thead>
<tr>
<th>名称</th>
<th>描述</th>
<th>值</th>
</tr>
</thead>
<tbody>
<tr>
<td>bgcolor</td>
<td>文本背景色</td>
<td>#f00 , rgba(0,0,0,0.5) , transparent</td>
</tr>
<tr>
<td>fillColor</td>
<td>文本颜色</td>
<td>#f00 , rgba(0,0,0,0.5) , transparent</td>
</tr>
<tr>
<td>fontSize</td>
<td>文本大小(px)</td>
<td>12</td>
</tr>
<tr>
<td>fontFamily</td>
<td>字体</td>
<td>'Arial'</td>
</tr>
<tr>
<td>fontWeight</td>
<td>文本加粗</td>
<td>'bold',800,300</td>
</tr>
<tr>
<td>anchor</td>
<td>坐标中心</td>
<td>[0,0]</td>
</tr>
<tr>
<td>padding</td>
<td>文字到包围盒边距(px),同 css[上，右，下，左]</td>
<td>[2,4,5,6]</td>
</tr>
<tr>
<td>opacity</td>
<td>透明度(0~1)</td>
<td>1</td>
</tr>
<tr>
<td>translate</td>
<td>位置变换(像素数组)</td>
<td>[-10，-10]</td>
</tr>
<tr>
<td>display</td>
<td>展现类型'none'为不渲染</td>
<td>'','none'</td>
</tr>
</tbody>
</table>
</section></template><script>export default {components:{}}</script>